import { useState } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { Schedulable } from '../../types';

import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import FormHelperText from '@mui/material/FormHelperText';

interface SharedScheduleStepProps {
  control: Control<Schedulable>;
  setValue: UseFormSetValue<Schedulable>;
  watch: UseFormWatch<Schedulable>;
  errors: FieldErrors<Schedulable>;
  isReport?: boolean;
  processName?: string;
}

export default function SharedScheduleStep({
  control,
  setValue,
  watch,
  errors,
  isReport,
  processName = 'item',
}: SharedScheduleStepProps) {
  const times = watch('times') || [];
  const dates = watch('dates') || [];
  const [currentTime, setCurrentTime] = useState(0);

  const daysOfTheWeek = [
    'SUNDAY',
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
  ];

  const months = [
    'JANUARY',
    'FEBRUARY',
    'MARCH',
    'APRIL',
    'MAY',
    'JUNE',
    'JULY',
    'AUGUST',
    'SEPTEMBER',
    'OCTOBER',
    'NOVEMBER',
    'DECEMBER',
  ];

  function containsDates(array1: string[], array2: string[]) {
    for (let i = 0; i < array1.length; i++) {
      if (array2.includes(array1[i])) {
        return true;
      }
    }
    return false;
  }

  const [dateType, setDateType] = useState(() =>
    containsDates(daysOfTheWeek, dates)
      ? 'week'
      : containsDates(months, dates)
      ? 'month'
      : 'days'
  );

  return (
    <Box>
      <Stack flexDirection="row" sx={{ pt: 1 }} gap={4}>
        <Stack gap={2}>
          <Stack flexDirection="row" gap={2}>
            <Box sx={{ width: 200 }}>
              <TimePicker
                label="Time"
                defaultValue={dayjs(new Date().setHours(0))}
                onChange={(event) => {
                  if (event) {
                    setCurrentTime(event.hour());
                  }
                }}
                views={['hours']}
                slotProps={{
                  textField: {
                    error: !!errors.times,
                    helperText: errors.times?.message
                      ? errors.times.message
                      : ' ',
                  },
                }}
              />
            </Box>
            <Box>
              <Button
                sx={{ height: 40 }}
                variant="contained"
                onClick={() => {
                  if (!times.includes(currentTime.toString())) {
                    setValue('times', [...times, currentTime.toString()], {
                      shouldValidate: true,
                    });
                  }
                }}
              >
                Add
              </Button>
            </Box>
          </Stack>
          <Typography>Times Selected: </Typography>
          <Grid container spacing={1}>
            {times.map((time) => (
              <Grid size={{ xs: 4 }} key={time}>
                <Controller
                  name="times"
                  control={control}
                  render={({ field }) => (
                    <Chip
                      label={
                        parseInt(time) === 0
                          ? '12 AM'
                          : parseInt(time) < 12
                          ? `${time} AM`
                          : parseInt(time) === 12
                          ? `${time} PM`
                          : `${parseInt(time) - 12} PM`
                      }
                      onDelete={() => {
                        if (!field.value?.includes(time)) {
                          setValue('times', [...times, time]);
                        } else {
                          setValue(
                            'times',
                            times.filter((value) => value !== time)
                          );
                        }
                      }}
                      color={'primary'}
                      size="medium"
                    />
                  )}
                />
              </Grid>
            ))}
          </Grid>
        </Stack>
        <Divider orientation="vertical" sx={{ height: 400 }} />
        <Stack gap={1}>
          {isReport ? (
            <FormControl margin="none">
              <Stack sx={{ width: 200 }}>
                <Controller
                  name="scheduleEndDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      value={field.value ? dayjs(field.value) : null}
                      label="End Date"
                      onChange={(date) => {
                        field.onChange(
                          date && date.isValid() ? date.valueOf() : 0
                        );
                      }}
                      disablePast
                    />
                  )}
                />
              </Stack>
              <FormHelperText sx={{ mt: 1, width: 400 }}>
                Define the optional end date for your {processName}. Once this
                date is reached, the {processName} will transition to a
                "non-scheduled" state
              </FormHelperText>
            </FormControl>
          ) : null}
          {!isReport ? (
            <FormControl margin="none">
              <Stack sx={{ width: 200 }}>
                <Controller
                  name="scheduleEndDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      value={field.value ? dayjs(field.value) : null}
                      label="Expiration Date"
                      onChange={(date) => {
                        field.onChange(
                          date && date.isValid() ? date.valueOf() : 0
                        );
                      }}
                      disablePast
                    />
                  )}
                />
              </Stack>
              <FormHelperText sx={{ mt: 1, width: 400 }}>
                Define an optional expiration date for your {processName}. Once
                this date is reached, the {processName} will transition to a
                "non-scheduled" state
              </FormHelperText>
            </FormControl>
          ) : null}
          <FormControl error={!!errors.dates}>
            <FormLabel>Date Selector</FormLabel>
            <RadioGroup
              aria-labelledby="radio-buttons-group-label"
              value={dateType}
              onChange={(event) => {
                setDateType(event.target.value);
                setValue('dates', []);
              }}
              name="radio-buttons-group"
              row
            >
              <FormControlLabel
                value="week"
                control={<Radio />}
                label="Week Days"
              />
              <FormControlLabel
                value="month"
                control={<Radio />}
                label="Months"
              />
              <FormControlLabel
                value="days"
                control={<Radio />}
                label="Month Days"
              />
            </RadioGroup>
            {errors.dates?.message ? (
              <FormHelperText error={!!errors.dates}>
                {errors.dates.message}
              </FormHelperText>
            ) : null}
          </FormControl>
          {dateType === 'week' ? (
            <Grid container spacing={2}>
              {daysOfTheWeek.map((day) => (
                <Grid size={{ xs: 4 }} key={day}>
                  <Controller
                    name="dates"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value?.includes(day)}
                            onChange={() => {
                              if (!field.value?.includes(day)) {
                                setValue('dates', [...dates, day], {
                                  shouldValidate: true,
                                });
                              } else {
                                setValue(
                                  'dates',
                                  dates.filter((value) => value !== day)
                                );
                              }
                            }}
                          />
                        }
                        label={
                          day.charAt(0).toUpperCase() +
                          day.slice(1).toLowerCase()
                        }
                      />
                    )}
                  />
                </Grid>
              ))}
            </Grid>
          ) : null}
          {dateType === 'month' ? (
            <Grid container spacing={2}>
              {months.map((month) => (
                <Grid size={{ xs: 4 }} key={month}>
                  <Controller
                    name="dates"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value?.includes(month)}
                            onChange={() => {
                              if (!field.value?.includes(month)) {
                                setValue('dates', [...dates, month], {
                                  shouldValidate: true,
                                });
                              } else {
                                setValue(
                                  'dates',
                                  dates.filter((value) => value !== month)
                                );
                              }
                            }}
                          />
                        }
                        label={
                          month.charAt(0).toUpperCase() +
                          month.slice(1).toLowerCase()
                        }
                      />
                    )}
                  />
                </Grid>
              ))}
            </Grid>
          ) : null}
          {dateType === 'days' ? (
            <Grid container spacing={1}>
              {[...Array(31).keys()].map((day) => (
                <Grid size={{ xs: 1.7 }} key={day}>
                  <Controller
                    name="dates"
                    control={control}
                    render={({ field }) => (
                      <Button
                        variant="contained"
                        color={
                          !field.value!.includes((day + 1).toString())
                            ? 'secondary'
                            : 'primary'
                        }
                        onClick={() => {
                          if (!field.value!.includes((day + 1).toString())) {
                            setValue(
                              'dates',
                              [...dates, (day + 1).toString()],
                              {
                                shouldValidate: true,
                              }
                            );
                          } else {
                            setValue(
                              'dates',
                              dates.filter(
                                (value) => value !== (day + 1).toString()
                              )
                            );
                          }
                        }}
                      >
                        {day + 1}
                      </Button>
                    )}
                  />
                </Grid>
              ))}
            </Grid>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
}
